import type { ProcessImportSyncResponse } from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/bulkprograminvitations/v1/bulk_program_invitations_api';

import type {
  FailedInvite,
  InviteLearnersResult,
  SuccessfulInvite,
} from 'bundles/enterprise-admin-learners/hooks/useInviteLearners';

import _t from 'i18n!nls/enterprise-admin-learners';

/**
 * These group-routing failure reasons only ever appear in `failureReasonCounts`, not the
 * older typed fields below — without this, they'd vanish from both `successful` and `failed`.
 */
const GROUP_FAILURE_REASON_KEYS = [
  'group_name_invalid',
  'group_ambiguous',
  'group_not_manageable',
  'group_absent_for_row',
] as const;

const groupFailureReasonMessage = (key: (typeof GROUP_FAILURE_REASON_KEYS)[number]): string => {
  switch (key) {
    case 'group_name_invalid':
      return _t("This learner's Group value didn't match any group in your organization.");
    case 'group_ambiguous':
      return _t("This learner's Group value matched more than one group in your organization.");
    case 'group_not_manageable':
      return _t('You do not have permission to manage learners in this group.');
    case 'group_absent_for_row':
      return _t('This row has no Group value, but other rows in this CSV specify one.');
    default:
      return key;
  }
};

/**
 * `newInvitationIds`/`newValidInviteeEmails` are parallel arrays — no other way to pair an id
 * with its email here. Skipped/existing counts and the group-routing failure reasons above
 * have no email, so they're padded in as blank-email `failed` entries to keep the total count
 * matching `getSkippedCount`'s math.
 */
export const adaptCsvSyncResponse = (
  data: ProcessImportSyncResponse,
  programId: string | undefined
): InviteLearnersResult => {
  const successful: SuccessfulInvite[] = data.newValidInviteeEmails.map((email, index) => ({
    email,
    programInvitationId: data.newInvitationIds[index] ?? '',
    firstInvitation: true,
  }));

  const failed: FailedInvite[] = [
    ...data.inviteesWithInvalidEmails.map((email) => ({
      email,
      errorCode: 'INVALID_EMAIL',
      message: _t('This email address is invalid.'),
    })),
    ...data.inviteesWithInvalidNames.map((email) => ({
      email,
      errorCode: 'INVALID_NAME',
      message: _t('This learner is missing a valid name.'),
    })),
    ...data.inviteesWithInvalidExternalId.map((email) => ({
      email,
      errorCode: 'INVALID_EXTERNAL_ID',
      message: _t('This learner has an invalid external ID.'),
    })),
    ...Array.from({ length: data.skippedMemberCount + data.existingInvitationCount }, () => ({
      email: '',
      errorCode: 'SKIPPED',
      message: _t('This learner was already part of the program or already invited.'),
    })),
    ...GROUP_FAILURE_REASON_KEYS.flatMap((key) =>
      Array.from({ length: data.failureReasonCounts?.[key] ?? 0 }, () => ({
        email: '',
        errorCode: key.toUpperCase(),
        message: groupFailureReasonMessage(key),
      }))
    ),
  ];

  return {
    successful,
    failed,
    membershipEmails: data.newValidMemberEmails,
    programId,
  };
};
